import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Home, BookOpen, Coins } from 'lucide-react';

export default function SessionComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state?.result;

  useEffect(() => {
    if (data && data.score >= 7) {
      const duration = 2000;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#FF6B35', '#FFD23F', '#06D6A0'] });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#FF6B35', '#FFD23F', '#06D6A0'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
        <div className="text-center">
          <p className="font-bold" style={{ color: '#1B4F8A' }}>No session data found.</p>
          <button onClick={() => navigate('/home')} className="mt-4 px-6 py-3 rounded-full font-bold text-white" style={{ background: '#FF6B35' }}>Go Home</button>
        </div>
      </div>
    );
  }

  const score = data.score || 0;
  const total = data.total || 10;
  const streak = data.streak || {};
  const badges = data.new_badges || [];
  const coinsEarned = data.coins_earned || 0;
  const totalCoins = data.total_coins || 0;

  const getMessage = () => {
    if (score === total) return { text: 'PERFECT! You\'re unstoppable!', emoji: '🏆', color: '#FFD23F' };
    if (score >= 8) return { text: 'Excellent work! Almost perfect!', emoji: '⭐', color: '#FF6B35' };
    if (score >= 6) return { text: 'Good job! Keep practicing!', emoji: '💪', color: '#06D6A0' };
    return { text: 'Don\'t give up! Tomorrow is a new day!', emoji: '🌟', color: '#1B4F8A' };
  };
  const msg = getMessage();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#FFF8F0' }}>
      <div className="w-full max-w-md text-center animate-bounce-in">
        {/* Trophy/Star */}
        <div className="text-8xl mb-4">{msg.emoji}</div>

        {/* Score */}
        <div className="bg-white border-2 rounded-3xl p-6 mb-4" style={{ borderColor: '#E5DACE', boxShadow: '0 4px 0 #E5DACE' }} data-testid="session-score-card">
          <p className="text-5xl font-black mb-2" style={{ color: msg.color }}>
            {score} / {total}
          </p>
          <p className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>{msg.text}</p>
        </div>

        {/* MathCoins Earned */}
        <div className="bg-[#FFD23F]/15 border-2 border-[#FFD23F] rounded-2xl p-4 mb-4 text-center" data-testid="coins-earned">
          <div className="flex items-center justify-center gap-2">
            <Coins size={24} color="#FFD23F" />
            <p className="text-2xl font-black" style={{ color: '#1B4F8A' }}>+{coinsEarned}</p>
          </div>
          <p className="text-xs font-bold mt-1" style={{ color: '#8B7355' }}>MathCoins earned! Total: {totalCoins}</p>
        </div>

        {/* Streak */}
        <div className="bg-white border-2 rounded-2xl p-4 mb-4 flex items-center justify-center gap-3" style={{ borderColor: '#E5DACE' }} data-testid="streak-update">
          <span className="text-3xl">🔥</span>
          <p className="text-lg font-extrabold" style={{ color: '#FF6B35' }}>Streak: {streak.current_streak || 0} days!</p>
        </div>

        {/* New Badges */}
        {badges.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="new-badges">
            {badges.map((b, i) => (
              <div key={i} className="bg-[#FFD23F]/20 border-2 border-[#FFD23F] rounded-2xl p-3 animate-slide-up"
                style={{ animationDelay: `${i * 200}ms` }}>
                <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>
                  🏅 New Badge: {b.badge_name} — {b.badge_description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button data-testid="go-home-btn" onClick={() => navigate('/home', { replace: true })}
            className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base"
            style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918' }}>
            <Home size={18} className="inline mr-2" /> Go Home
          </button>
          <button data-testid="see-progress-btn" onClick={() => navigate('/progress')}
            className="btn-press w-full py-4 rounded-full font-extrabold text-sm border-2"
            style={{ borderColor: '#E5DACE', color: '#1B4F8A', boxShadow: '0 4px 0 #E5DACE' }}>
            <BookOpen size={16} className="inline mr-2" /> See My Progress
          </button>
        </div>
      </div>
    </div>
  );
}
